import { ArrowLeftCircleIcon, ArrowRightCircleIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getAdminConfig } from 'services'
import { combinedMonthlyExpense } from 'services/pdfs/utils'
import { getOpenLoanDisplayNo, thousandSeperator } from 'utils'
import { isConstructionLoan } from 'utils/isConstructionLoan'

import { SubmenuItem, subMenus } from '../../../pages/Admin/AdminTools/Configuration/Features/constant'
import { floaterPanelEdgeClass, floaterTabEdgeClass, FloaterTabWrapper, useFloaterTab } from './useFloaterTab'

const CONSTRUCTION_ONLY_KEYS = new Set(['budget'])

const buildExternalRoutes = (loanNumber: number | string): Record<string, string> => ({
  tasks: `/tasks/loan/${loanNumber}`,
  reminders: `/reminders/${loanNumber}`,
  loanStructure: `/loan_process/structure/${loanNumber}`,
  loanApplication: `/loan_process/application/${loanNumber}`,
  loanSubmission: `/loan_process/submit/${loanNumber}`,
})

export const UnderwritingSlide = () => {
  const { loanDetail, loan } = useSelector((state: any) => ({
    loanDetail: state.loanDetail,
    loan: state.loan,
  }))
  const [isShowSlide, setShowSlide] = useState(false)
  const floater = useFloaterTab('underwriting', { side: 'right', percent: 25, offset: -60, mobileTop: 80 })
  const [data, setData] = useState<any>({})
  const [total, setTotal] = useState('')
  const [configSubmenus, setConfigSubmenus] = useState<SubmenuItem[]>([])

  useEffect(() => {
    const payments = combinedMonthlyExpense(loan, loanDetail.rateData)
    setData({
      [payments['title']]: `$ ${payments['mortgagePayment']}`,
      "Homeowner's Insurance": `$ ${payments['proposedMonthlyInsurance']}`,
      'Property Taxes': `$ ${payments['proposedMonthlyTaxes']}`,
      HOA: `$ ${payments['proposedMonthlyHoaDues']}`,
      'Additional Lien Payment': `$ ${payments['secondLienPayment']}`,
      'Flood Insurance': `$ ${payments.floodInsurance}`,
      'School Tax': `$ ${payments.schoolTax}`,
      'Other Tax': `$ ${payments.otherTax}`,
    })
    setTotal(payments['sum'])
  }, [loan, loanDetail])

  useEffect(() => {
    getAdminConfig('underwritingQuickLinks').then((res) => {
      if (res && Array.isArray(res.submenus)) setConfigSubmenus(res.submenus)
    })
  }, [])

  const loanNumber = loanDetail?.loanNumber
  const externalRoutes = buildExternalRoutes(loanNumber)

  const onShowSlide = () => setShowSlide(!isShowSlide)

  const handleClick = (submenuItem: SubmenuItem) => {
    const isExternal = submenuItem.isExternal || !!externalRoutes[submenuItem.key]
    if (isExternal) {
      const url = externalRoutes[submenuItem.key] || `/${submenuItem.key}/${loanNumber}`
      window.open(url, '_blank', 'resizable=yes')
    } else {
      window.open(`/uw_link_page/${submenuItem.key}/${loanNumber}`, '_blank', 'resizable=yes')
    }
  }

  const getTitle = (submenuItem: SubmenuItem): string => {
    if (submenuItem.title) return submenuItem.title
    return subMenus[submenuItem.key as keyof typeof subMenus]?.title || submenuItem.key
  }

  return (
    <>
      <FloaterTabWrapper floater={floater}>
        <button
          className={`p-3 ${floaterTabEdgeClass(
            floater.side,
          )} ring-[1px] ring-shade-blue bg-shade-blue text-white border hover:bg-white border-shade-blue hover:text-shade-blue rounded flex items-center gap-2 w-auto sm:w-[155px]`}
          {...floater.tabProps(onShowSlide)}
        >
          <ArrowTopRightOnSquareIcon className="w-6 h-6" />
          <span className="hidden sm:inline">Underwriting</span>
        </button>
      </FloaterTabWrapper>
      {isShowSlide && (
        <div
          className={`shadow1 fixed bg-white border z-50 top-[5vh] bottom-0 w-[24rem] max-w-full h-[90vh] overflow-auto ease-in-out ${floaterPanelEdgeClass(
            floater.side,
          )}`}
        >
          <div className="w-[24rem] max-w-full fixed z-20 rounded-t flex justify-between p-3 font-bold text-sm place-items-center items-center text-shade-blue border-b bg-white">
            <div className="flex items-center">
              <p className="text-lg text-gray-800">Underwriting: </p>
              <span className="text-[16px] ml-2 italic font-semibold">
                {getOpenLoanDisplayNo(loanDetail)}
              </span>
            </div>
            <button className="hover:underline hover-shadow1 p-1 rounded" onClick={onShowSlide}>
              {floater.side === 'left' ? (
                <ArrowLeftCircleIcon className="w-5 h-5" />
              ) : (
                <ArrowRightCircleIcon className="w-5 h-5" />
              )}
            </button>
          </div>
          <div className="pt-14 h-fit overflow-auto relative text-sm text-gray-900">
            <ol className="ml-2 pl-4">
              {configSubmenus.map((submenuItem, index) => {
                if (CONSTRUCTION_ONLY_KEYS.has(submenuItem.key) && !isConstructionLoan(loan?.productType)) return null
                const title = getTitle(submenuItem)
                return (
                  <li
                    key={`${submenuItem.key}-${index}`}
                    className="my-[7px] hover:underline w-fit cursor-pointer"
                    onClick={() => handleClick(submenuItem)}
                  >
                    <span className="flex gap-2 items-center">
                      <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      <span>{title}</span>
                    </span>
                  </li>
                )
              })}
            </ol>
            <p className="bg-blue-100 text-shade-blue font-bold p-3 mt-4">Proposed Monthly Property Payment</p>
            <table className="w-full">
              <tbody>
                {Object.keys(data).map((key, index: number) => (
                  <tr className="border-b" key={index}>
                    <td className="px-3 py-2">{key}:</td>
                    <td className="px-3 py-2">{data[key]}</td>
                  </tr>
                ))}
                <tr className="border-b">
                  <td className="px-3 py-2 text-center font-bold">Total:</td>
                  <td className="px-3 py-2 text-shade-blue font-bold">$ {thousandSeperator(total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  )
}
